import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "@/context/ClientAuthContext";
import { useLang } from "@/context/LanguageContext";

export default function ClientLoginPage() {
  const { t } = useLang();
  const { login, user, checking } = useClientAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (!checking && user) return <Navigate to="/portal" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success(t("portal.welcomeBack"));
      navigate("/portal");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("portal.loginFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-radial-gold grid place-items-center px-4">
      <div className="w-full max-w-md card-dark p-8 sm:p-10 gold-glow" data-testid="client-login-card">
        <div className="text-center mb-8">
          <img
            src="/logo.png"
            alt="Eazy-Travels"
            className="h-14 w-auto object-contain mx-auto mb-5 drop-shadow-[0_0_30px_rgba(244,196,48,0.35)]"
          />
          <h1 className="font-display text-2xl font-bold text-white mb-1">{t("portal.title")}</h1>
          <p className="text-sm text-white/55">{t("portal.subtitle")}</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("contact.email")}</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-dark pl-10"
                data-testid="client-email-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">{t("admin.password")}</label>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--gold)]" />
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark pl-10"
                data-testid="client-password-input"
              />
            </div>
          </div>
          <button type="submit" disabled={submitting} className="btn-gold w-full justify-center" data-testid="client-login-submit">
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {t("portal.signIn")}
          </button>
        </form>

        <div className="flex items-center justify-between mt-6 text-xs text-white/50">
          <Link to="/portal/forgot-password" className="hover:text-[var(--gold)] transition">{t("portal.forgotPassword")}</Link>
          <Link to="/portal/signup" className="hover:text-[var(--gold)] transition">{t("portal.createAccount")}</Link>
        </div>
      </div>
    </div>
  );
}
